import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * ── The truck report's chart pieces ──────────────────────────────────────────
 *
 * Data colours come from one validated set (run through the dataviz palette
 * checks against the white card surface), and every chart reads against these
 * roles rather than raw hex:
 *
 *   · one series                → `series1` (blue)
 *   · full vs empty             → `series1` / `series2` (blue / orange), with
 *                                  "not stated" in the neutral gray
 *   · not started → under way → done — an ordered scale, so one hue light→dark
 *
 * Text never wears a data colour; identity is carried by the swatch beside it,
 * and every value a bar shows is also written out in its legend or the table.
 */
export const VIZ = {
  series1: "#2a78d6",
  series2: "#eb6834",
  neutral: "#c3c2b7",
  progress: { notStarted: "#86b6ef", inProgress: "#3987e5", completed: "#184f95" },
  grid: "#e1e0d9",
  axis: "#898781",
  ink: "#0b0b0b",
  inkSecondary: "#52514e",
} as const;

/** A dashboard card: title, optional subtitle, content. */
export function Card({
  title,
  subtitle,
  action,
  children,
  className = "",
  flush = false,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Content runs edge to edge (a table); only the header keeps the padding. */
  flush?: boolean;
}) {
  return (
    <section
      className={`min-w-0 overflow-hidden rounded-xl border border-black/10 bg-white ${flush ? "" : "p-4"} ${className}`}
    >
      <header className={`mb-3 flex items-start justify-between gap-3 ${flush ? "px-4 pt-4" : ""}`}>
        <div className="min-w-0">
          <h2 className="text-[13px] font-semibold text-[#0b0b0b]">{title}</h2>
          {subtitle && <p className="mt-0.5 text-[11.5px] text-[#52514e]">{subtitle}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/** The one number the page leads with. */
export function HeroFigure({
  label,
  value,
  unit,
  caption,
  footer,
  warning,
}: {
  label: string;
  value: string;
  unit: string;
  caption: string;
  footer: ReactNode;
  warning?: string | null;
}) {
  return (
    <section className="flex min-w-0 flex-col rounded-xl border border-black/10 bg-white p-5">
      <div className="text-[12px] font-semibold text-[#52514e]">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-[40px] font-semibold leading-none tracking-tight text-[#0b0b0b] sm:text-[52px]">
          {value}
        </span>
        <span className="text-[18px] font-semibold text-[#52514e]">{unit}</span>
      </div>
      <p className="mt-2 text-[11.5px] text-[#898781]">{caption}</p>
      {warning && (
        <p className="mt-2 flex items-start gap-1.5 text-[11.5px] font-medium text-amber-800">
          <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {warning}
        </p>
      )}
      <div className="mt-auto border-t border-[#e1e0d9] pt-3">{footer}</div>
    </section>
  );
}

/** A stat tile: label, value, one line of context. */
export function StatTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-xl border border-black/10 bg-white px-4 py-3.5">
      <div className="text-[12px] font-semibold text-[#52514e]">{label}</div>
      <div className="mt-1 text-[28px] font-semibold leading-tight text-[#0b0b0b]">{value}</div>
      {hint && <div className="mt-0.5 truncate text-[11.5px] text-[#898781]">{hint}</div>}
    </div>
  );
}

export interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
}

/**
 * Part-to-whole as one bar, with the legend carrying every count and share —
 * the bar is the glance, the legend is the reading.
 */
export function StackedBar({
  segments,
  format,
}: {
  segments: Segment[];
  format: (value: number) => string;
}) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const shown = segments.filter((s) => s.value > 0);
  return (
    <div>
      <div
        className="flex h-3 w-full gap-[2px] overflow-hidden rounded bg-[#f0efec]"
        role="img"
        aria-label={segments.map((s) => `${s.label} ${s.value}`).join(", ")}
      >
        {shown.map((s) => (
          <div
            key={s.key}
            title={`${s.label}: ${format(s.value)} (${Math.round((s.value / total) * 100)}%)`}
            className="h-full transition-opacity hover:opacity-80"
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
          />
        ))}
      </div>
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5 text-[11.5px] text-[#52514e]">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} aria-hidden />
            {s.label}
            <span className="font-semibold tabular-nums text-[#0b0b0b]">{format(s.value)}</span>
            {total > 0 && (
              <span className="tabular-nums text-[#898781]">
                {Math.round((s.value / total) * 100)}%
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A compact stacked bar for a table cell — no legend; the cell's text says the numbers. */
export function MiniStackedBar({ segments }: { segments: Segment[] }) {
  const total = segments.reduce((sum, s) => sum + s.value, 0);
  if (!total) return null;
  return (
    <div className="flex h-2 w-full gap-[2px] overflow-hidden rounded-sm bg-[#f0efec]">
      {segments
        .filter((s) => s.value > 0)
        .map((s) => (
          <div
            key={s.key}
            title={`${s.label}: ${s.value}`}
            className="h-full"
            style={{ width: `${(s.value / total) * 100}%`, background: s.color }}
          />
        ))}
    </div>
  );
}

/** Ranked horizontal bars — one series, value at the tip. */
export function RankedBars({
  rows,
  format,
}: {
  rows: { key: string; label: string; value: number; muted?: boolean }[];
  format: (value: number) => string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-2">
      {rows.map((row) => (
        <li key={row.key} className="grid grid-cols-[minmax(0,7rem)_1fr] items-center gap-2">
          <span className="truncate text-[11.5px] text-[#52514e]" title={row.label}>
            {row.label}
          </span>
          <div className="flex items-center gap-2" title={`${row.label}: ${format(row.value)}`}>
            <div
              className="h-2.5 rounded-r"
              style={{
                width: `${Math.max(2, (row.value / max) * 100)}%`,
                background: row.muted ? VIZ.neutral : VIZ.series1,
              }}
            />
            <span className="shrink-0 text-[11.5px] font-semibold tabular-nums text-[#0b0b0b]">
              {format(row.value)}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** A value bar inside a table cell, scaled against the largest in the column. */
export function InlineBar({ value, max, children }: { value: number; max: number; children: ReactNode }) {
  return (
    <div className="flex min-w-[180px] items-center gap-3">
      <div className="h-2 flex-1">
        <div
          className="h-2 rounded-r"
          style={{ width: `${max ? Math.max(2, (value / max) * 100) : 0}%`, background: VIZ.series1 }}
        />
      </div>
      <div className="w-24 shrink-0 whitespace-nowrap text-right">{children}</div>
    </div>
  );
}

/** Containers worked per day (or week) across the range — one series of columns. */
export function DailyColumns({
  data,
  valueLabel,
  format,
}: {
  data: { key: string; label: string; tooltipLabel: string; value: number }[];
  valueLabel: string;
  format: (value: number) => string;
}) {
  return (
    <div className="h-[240px] w-full lg:h-[330px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: -18 }}>
          <CartesianGrid vertical={false} stroke={VIZ.grid} strokeWidth={1} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={{ stroke: VIZ.neutral }}
            tick={{ fill: VIZ.axis, fontSize: 11 }}
            interval="preserveStartEnd"
            minTickGap={12}
          />
          <YAxis
            allowDecimals={false}
            tickLine={false}
            axisLine={false}
            tick={{ fill: VIZ.axis, fontSize: 11 }}
            width={42}
          />
          <Tooltip
            cursor={{ fill: "rgba(11,11,11,0.04)" }}
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload as (typeof data)[number] | undefined;
              if (!active || !point) return null;
              return (
                <div className="rounded-lg border border-black/10 bg-white px-3 py-2 shadow-lg">
                  <div className="text-[15px] font-semibold tabular-nums text-[#0b0b0b]">
                    {format(point.value)}
                  </div>
                  <div className="text-[11px] text-[#52514e]">
                    {valueLabel} · {point.tooltipLabel}
                  </div>
                </div>
              );
            }}
          />
          {/* No grow-in animation: a refetch should swap numbers, not replay the chart. */}
          <Bar
            dataKey="value"
            fill={VIZ.series1}
            radius={[4, 4, 0, 0]}
            maxBarSize={24}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
